import React, { useState, useCallback, useRef, useEffect } from 'react';
import I18n from 'i18n-js';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, EffectFade } from 'swiper/modules';
import classNames from 'classnames';
import { RCIcon, RCImage, useMediaQuery } from '@reverbdotcom/cadence/components';
import { ChevronLeftIcon, ChevronRightIcon } from '@reverbdotcom/cadence/icons/react';
import CoreLink from './core_link';
import { MParticleEvent } from '../elog/mparticle_types';

interface Props {
  images: string[];
  imgAlt?: string;
  bumpInteractionTracking?: (componentName: string) => void;
  parentComponentName?: string;
  enableSwiper?: boolean;
  navigateToUrl?: string;
  clickEvent?: MParticleEvent;
}

const ANIMATION_DURATION = 200;
const RIGHT = 'right';
const LEFT = 'left';

/**
 * ImageControls are hidden for screen readers until
 * we can support user submitted alt-text.
 */
function ImageControls(props: {
  hide: boolean,
  onClickLeft: (e: any) => void,
  onClickRight: (e: any) => void
}) {
  const { onClickLeft, onClickRight, hide } = props;
  if (hide) { return null; }

  return (
    <>
      <div className={`image-carousel__control-wrapper image-carousel__control-wrapper--${LEFT}`}>
        <button
          onClick={onClickLeft}
          className="image-carousel__control"
          tabIndex={-1}
          aria-label={I18n.t('commons.previousImage')}
          aria-hidden="true"
          type="button"
        >
          <RCIcon svgComponent={ChevronLeftIcon} />
        </button>
      </div>
      <div className={`image-carousel__control-wrapper image-carousel__control-wrapper--${RIGHT}`}>
        <button
          onClick={onClickRight}
          className="image-carousel__control"
          tabIndex={-1}
          aria-label={I18n.t('commons.nextImage')}
          aria-hidden="true"
          type="button"
        >
          <RCIcon svgComponent={ChevronRightIcon} />
        </button>
      </div>
    </>
  );
}

function CloudinaryImage(props: { image: string, alt: string }) {
  if (!props.image) { return null; }

  return (
    <RCImage
      src={props.image}
      alt={props.alt}
      key={props.image}
      overlay="offwhite"
      imageBox="auto"
      borderRadius="lg"
    />
  );
}

function ImageSlide(props: { path: string, alt: string }) {
  return (
    <div className="image-carousel__slide">
      <CloudinaryImage image={props.path} alt={props.alt} />
    </div>
  );
}

function ImageCarousel({
  images,
  imgAlt = '',
  bumpInteractionTracking,
  parentComponentName,
  enableSwiper,
  navigateToUrl,
  clickEvent,
}: Props) {
  const [nextIndex, setNextIndex] = useState(0);
  const [index, setIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationClass, setAnimationClass] = useState('');
  const [clicked, setClicked] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMobile = useMediaQuery('mobile');
  const isTablet = useMediaQuery('tablet');
  const useMobileSwiper = enableSwiper && (isMobile || isTablet) && images.length > 1;

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const getNextIndex = useCallback((dir: string) => {
    const max = images.length - 1;
    const next = dir === RIGHT ? index + 1 : index - 1;

    if (next < 0) { return max; }
    if (next > max) { return 0; }

    return next;
  }, [images.length, index]);

  const scroll = useCallback((dir: string) => {
    if (isAnimating) { return; }

    const newAnimationClass = `image-carousel--click-${dir}`;
    const newNextIndex = getNextIndex(dir);

    setAnimationClass(newAnimationClass);
    setNextIndex(newNextIndex);
    setIsAnimating(true);
    setClicked(true);

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(
      () => {
        setAnimationClass('');
        setIndex(newNextIndex);
        setIsAnimating(false);
        timeoutRef.current = null;
      },
      ANIMATION_DURATION,
    );
  }, [isAnimating, getNextIndex]);

  const onClickLeft = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    scroll(LEFT);

    if (bumpInteractionTracking !== undefined) {
      bumpInteractionTracking(`${parentComponentName}:prevImage`);
    }
  }, [scroll, bumpInteractionTracking, parentComponentName]);

  const onClickRight = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    scroll(RIGHT);

    if (bumpInteractionTracking !== undefined) {
      bumpInteractionTracking(`${parentComponentName}:nextImage`);
    }
  }, [scroll, bumpInteractionTracking, parentComponentName]);

  // renders a hidden div with all of the images to force the browser
  // to fetch and cache.
  const renderPrefetch = useCallback(() => {
    if (!clicked) { return false; }

    return images.map((image) => {
      return <CloudinaryImage key={image} image={image} alt={imgAlt} />;
    });
  }, [clicked, images, imgAlt]);

  const image = images[index];
  const nextImage = isAnimating ? images[nextIndex] : null;

  return (
    <div className={classNames('image-carousel', animationClass)}>
      <div className="image-carousel__prefetch">
        {renderPrefetch()}
      </div>
      {useMobileSwiper && (
        <MobileImageCarousel
          images={images}
          imgAlt={imgAlt}
          navigateToUrl={navigateToUrl}
          clickEvent={clickEvent}
        />
      )}
      {!useMobileSwiper && (
        <>
          <div className="image-carousel__slides">
            <ImageSlide path={nextImage} alt={imgAlt} />
            <ImageSlide path={image} alt={imgAlt} />
            <ImageSlide path={nextImage} alt={imgAlt} />
          </div>
          <ImageControls
            hide={images.length <= 1}
            onClickLeft={onClickLeft}
            onClickRight={onClickRight}
          />
        </>
      )}
    </div>
  );
}

interface MobileImageCarouselProps {
  images: string[];
  imgAlt?: string;
  navigateToUrl?: string;
  clickEvent?: MParticleEvent;
}

function MobileImageCarousel({ images, imgAlt = '', navigateToUrl, clickEvent }: MobileImageCarouselProps) {
  return (
    <div className="image-carousel__swiper">
      <CoreLink
        to={navigateToUrl}
        key={navigateToUrl}
        clickEvent={clickEvent}
      >
        <Swiper
          modules={[Pagination, EffectFade]}
          pagination
          effect="fade"
          speed={ANIMATION_DURATION}
          slidesPerView={1}
          loop={images.length >= 3}
        >
          {images.map(image => (
            <SwiperSlide key={image} className="image-carousel__slide">
              <CloudinaryImage image={image} alt={imgAlt} />
            </SwiperSlide>
          ))}
        </Swiper>
      </CoreLink>
    </div>
  );
}

export default ImageCarousel;
